import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import LanguageSwitcher from './LanguageSwitcher';
import { IconHome, IconFilePlus, IconSearch, IconClipboardList, IconUser, IconCode, IconSettings, IconLogout, IconMenu, IconX, IconChevronDown, IconBell } from './icons';

// Determine if in development mode
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';

const Layout = ({ children, showHeader = false }) => {
    const { user, authenticated, logout } = useAuth();
    const { t } = useI18n(['common']);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    // Click outside to close user menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="min-h-screen bg-gray-50">
            {showHeader && authenticated && (
                <header className="bg-white shadow-sm">
                    <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex justify-between items-center">
                        <div className="flex items-center gap-6">
                            {/* Hamburger button - visible only on small screens */}
                            <button
                                type="button"
                                className="sm:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:bg-gray-100"
                                aria-controls="mobile-menu"
                                aria-expanded={mobileMenuOpen}
                                onClick={() => setMobileMenuOpen(v => !v)}
                            >
                                <span className="sr-only">Open menu</span>
                                {mobileMenuOpen ? (
                                    <IconX className="h-6 w-6" />
                                ) : (
                                    <IconMenu className="h-6 w-6" />
                                )}
                            </button>

                            <Link to="/" className="text-xl font-bold text-blue-600 hover:text-blue-800">
                                OA System
                            </Link>
                            <nav className="hidden sm:flex gap-4">
                                <Link to="/home" className="text-gray-700 hover:text-blue-600 flex items-center">
                                    <IconHome className="w-5 h-5 mr-1" />
                                    {t('nav.home')}
                                </Link>
                                <Link to="/forms" className="text-gray-700 hover:text-blue-600 flex items-center">
                                    <IconFilePlus className="w-5 h-5 mr-1" />
                                    {t('nav.forms')}
                                </Link>
                                <Link to="/forms/search" className="text-gray-700 hover:text-blue-600 flex items-center">
                                    <IconSearch className="w-5 h-5 mr-1" />
                                    {t('nav.search')}
                                </Link>
                                <Link to="/forms/approval" className="text-gray-700 hover:text-blue-600 flex items-center">
                                    <IconClipboardList className="w-5 h-5 mr-1" />
                                    {t('nav.approval')}
                                </Link>
                                {/* <Link to="/reminders" className="text-gray-700 hover:text-blue-600 flex items-center">
                                    <IconBell className="w-5 h-5 mr-1" />
                                    {t('nav.reminders')}
                                </Link> */}
                            </nav>
                        </div>
                        <div className="flex items-center gap-4">
                            {/* Language switcher */}
                            <LanguageSwitcher />
                            
                            {/* User dropdown menu */}
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 focus:outline-none cursor-pointer"
                                >
                                    <IconUser className="w-6 h-6" />
                                    <span className="hidden sm:inline">Hi, {user?.displayName || user?.username}</span>
                                    <IconChevronDown className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                                </button>

                                {/* Context menu */}
                                {userMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-52 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                                        <div className="px-4 py-2 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900">{user?.displayName || user?.username}</p>
                                            <p className="text-xs text-gray-500">{user?.email || user?.mail}</p>
                                        </div>
                                        
                                        <Link
                                            to="/profile"
                                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                            onClick={() => setUserMenuOpen(false)}
                                        >
                                            <IconUser className="w-4 h-4 inline-block mr-2" />
                                            {t('nav.profile')}
                                        </Link>
                                        
                                        {/* Système Configuration - Admin Only */}
                                        {(user?.admin || user?.roles?.includes('admin')) && (
                                            <Link
                                                to="/config"
                                                className="block px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                                                onClick={() => setUserMenuOpen(false)}
                                            >
                                                <IconSettings className="w-4 h-4 inline-block mr-2" />
                                                {t('nav.configManager')}
                                            </Link>
                                        )}
                                        
                                        <div className="border-t border-gray-100">
                                            <button
                                                onClick={() => {
                                                    setUserMenuOpen(false);
                                                    handleLogout();
                                                }}
                                                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                            >
                                                <IconLogout className="w-4 h-4 inline-block mr-2" />
                                                {t('nav.logout')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile dropdown menu */}
                    {mobileMenuOpen && (
                        <div id="mobile-menu" className="sm:hidden bg-white border-t border-gray-100">
                            <div className="px-4 pt-4 pb-4 space-y-1">
                                <Link to="/home" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                    <IconHome className="w-5 h-5 mr-2" />
                                    {t('nav.home')}
                                </Link>
                                <Link to="/forms" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                    <IconFilePlus className="w-5 h-5 mr-2" />
                                    {t('nav.forms')}
                                </Link>
                                <Link to="/forms/search" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                    <IconSearch className="w-5 h-5 mr-2" />
                                    {t('nav.search')}
                                </Link>
                                <Link to="/forms/approval" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                    <IconClipboardList className="w-5 h-5 mr-2" />
                                    {t('nav.approval')}
                                </Link>
                                <Link to="/reminders" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                    <IconBell className="w-5 h-5 mr-2" />
                                    {t('nav.reminders')}
                                </Link>
                                
                                <div className="border-t border-gray-200 pt-2 mt-2">
                                    <Link to="/profile" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
                                        <IconUser className="w-5 h-5 mr-2" />
                                        {t('nav.profile')}
                                    </Link>
                                    
                                    {/* Developer panel - development mode only */}
                                    {isDevelopment && (
                                        <Link to="/dev" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-orange-600 hover:bg-orange-50" onClick={() => setMobileMenuOpen(false)}>
                                            <IconCode className="w-5 h-5 mr-2" />
                                            {t('nav.devPanel')}
                                        </Link>
                                    )}
                                    
                                    {/* System Configuration - admin only */}
                                    {(user?.admin || user?.roles?.includes('admin')) && (
                                        <Link to="/config" className="flex items-center px-3 py-2 rounded-md text-base font-medium text-purple-600 hover:bg-purple-50" onClick={() => setMobileMenuOpen(false)}>
                                            <IconSettings className="w-5 h-5 mr-2" />
                                            {t('nav.configManager')}
                                        </Link>
                                    )}
                                    
                                    {/* Language switcher for mobile */}
                                    <div className="px-3 py-2">
                                        <LanguageSwitcher />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </header>
            )}
            <main className={showHeader ? '' : 'min-h-screen'}>
                {children}
            </main>
        </div>
    );
};

export default Layout;
